import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '@mdi/react';
import { mdiAccountTie, mdiDownload, mdiChevronDown, mdiChevronUp } from '@mdi/js';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import AppHeader from '../../components/AppHeader';
import FolderTabs from '../../components/FolderTabs';
import InstructorDetailModal from '../../components/classes/InstructorDetailModal';
import { classApi } from '../../api/classes';
import { organizationApi } from '../../api/organization';
import '../Common.css';
import '../Dashboard.css';
import '../header.css';
import './ClassInstructorsPage.css';

// ─── Assignment Tab ──────────────────────────────────────────────────────────

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function initials(displayName) {
    return (displayName || '')
        .split(' ')
        .filter(Boolean)
        .slice(0, 2)
        .map(w => w[0].toUpperCase())
        .join('');
}

const AssignmentTab = ({ user }) => {
    const navigate = useNavigate();
    const [terms, setTerms] = useState([]);
    const [admins, setAdmins] = useState([]);
    const [allClasses, setAllClasses] = useState([]); // flat list across all terms
    const [shortCourses, setShortCourses] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedAdmin, setSelectedAdmin] = useState(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        try {
            const [termsRes, adminsRes, scRes] = await Promise.all([
                classApi.getTerms(),
                user?.organisationId
                    ? organizationApi.getOrgAdmins(user.organisationId)
                    : Promise.resolve({ ok: false }),
                classApi.getShortCourses(),
            ]);

            const fetchedTerms = termsRes.ok ? (termsRes.data.terms || []) : [];
            if (!termsRes.ok) toast.error('Failed to load terms');

            const fetchedAdmins = adminsRes.ok ? (adminsRes.data.admins || []) : [];
            setTerms(fetchedTerms);
            setAdmins(fetchedAdmins);
            setShortCourses(scRes.ok ? (scRes.data.shortCourses || []) : []);

            // Load classes for the current term only (active, not past — terms sorted startDate desc)
            if (fetchedTerms.length > 0) {
                const currentTerm = fetchedTerms.find(t => t.isActive && !t.isPast) || fetchedTerms[0];
                const res = await classApi.getClasses(currentTerm.id);
                if (res.ok) {
                    setAllClasses((res.data.classes || []).map(c => ({ ...c, termId: currentTerm.id })));
                }
            }
        } finally {
            setIsLoading(false);
        }
    }, [user?.organisationId]);

    useEffect(() => { load(); }, [load]);

    // Called by the modal after a weekly class save or remove
    const handleChanged = (classId, newInstructors) => {
        setAllClasses(prev =>
            prev.map(c => c.id === classId ? { ...c, primaryInstructors: newInstructors } : c)
        );
    };

    // Called by the modal after a short course save or remove
    const handleScChanged = (scId, newInstructors) => {
        setShortCourses(prev =>
            prev.map(c => c.id === scId ? { ...c, primaryInstructors: newInstructors } : c)
        );
    };

    if (isLoading) return <p className="ci-loading">Loading...</p>;

    if (admins.length === 0) {
        return (
            <p className="ci-empty">
                No team members found. Add team members in{' '}
                <button className="ci-text-link" onClick={() => navigate('/settings')}>
                    Settings
                </button>.
            </p>
        );
    }

    return (
        <div className="ci-assign-panel">
            <p className="ci-hint">
                <strong>Primary instructors</strong> are assigned per class for the whole term and carry forward when a term is rolled over.
                For a one-off substitute, use the override instructor feature on the check-in screen. Their sessions will appear in the Reports tab.
            </p>

            <div className="ci-card-list">
                {admins.map(admin => {
                    const assigned = allClasses.filter(c =>
                        (c.primaryInstructors || []).includes(admin.accountId)
                    );
                    const assignedSc = shortCourses.filter(c =>
                        (c.primaryInstructors || []).includes(admin.accountId)
                    );
                    return (
                        <div
                            key={admin.accountId}
                            className="ci-member-card"
                            onClick={() => setSelectedAdmin(admin)}
                        >
                            <div className="ci-member-card-header">
                                <div className="ci-avatar">{initials(admin.displayName)}</div>
                                <div className="ci-member-info">
                                    <span className="ci-member-name">{admin.displayName}</span>
                                    <span className={`ci-member-role-badge${admin.isOwner ? ' owner' : ''}`}>
                                        {admin.isOwner ? 'Owner' : 'Team Member'}
                                    </span>
                                </div>
                            </div>
                            {assigned.length === 0 && assignedSc.length === 0 ? (
                                <p className="ci-unassigned">No classes assigned</p>
                            ) : (
                                <div className="ci-assigned-classes">
                                    {assigned.map(cls => (
                                        <div key={cls.id} className="ci-assigned-class-row">
                                            <span className="ci-assigned-class-name">{cls.name}</span>
                                            <span className="ci-assigned-class-schedule">
                                                {DAYS[cls.dayOfWeek]} · {cls.startTime}
                                            </span>
                                        </div>
                                    ))}
                                    {assignedSc.map(sc => (
                                        <div key={sc.id} className="ci-assigned-class-row">
                                            <span className="ci-assigned-class-name">{sc.name}</span>
                                            <span className="ci-assigned-class-schedule">Short Course</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {selectedAdmin && (
                <InstructorDetailModal
                    admin={selectedAdmin}
                    allClasses={allClasses}
                    terms={terms}
                    shortCourses={shortCourses}
                    onClose={() => setSelectedAdmin(null)}
                    onChanged={handleChanged}
                    onScChanged={handleScChanged}
                />
            )}

        </div>
    );
};

// ─── Reports Tab ─────────────────────────────────────────────────────────────

const PRESETS = [
    {
        id: 'week',
        label: 'This Week',
        from: () => { const d = new Date(); d.setDate(d.getDate() - d.getDay()); return d.toISOString().split('T')[0]; },
        to: () => new Date().toISOString().split('T')[0],
    },
    {
        id: 'month',
        label: 'This Month',
        from: () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0]; },
        to: () => new Date().toISOString().split('T')[0],
    },
    {
        id: 'all',
        label: 'All Time',
        from: () => '2020-01-01',
        to: () => new Date().toISOString().split('T')[0],
    },
];


const ReportsTab = () => {
    const [reportTab, setReportTab] = useState('attendance');

    const [activePreset, setActivePreset] = useState('week');
    const [startDate, setStartDate] = useState(() => {
        const d = new Date();
        d.setDate(d.getDate() - d.getDay());
        return d.toISOString().split('T')[0];
    });
    const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

    const [report, setReport] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const [subReport, setSubReport] = useState(null);
    const [isSubLoading, setIsSubLoading] = useState(false);

    const loadAttendanceReport = useCallback(async (from, to) => {
        setIsLoading(true);
        try {
            const r = await classApi.getInstructorReport(from, to);
            if (r.ok) setReport(r.data.instructors);
            else toast.error(r.data?.message || 'Failed to load attendance report');
        } catch {
            toast.error('Failed to load attendance report');
        } finally {
            setIsLoading(false);
        }
    }, []);

    const loadSubstitutionReport = useCallback(async (from, to) => {
        setIsSubLoading(true);
        try {
            const r = await classApi.getSubstitutionReport(from, to);
            if (r.ok) setSubReport(r.data.substitutes);
            else toast.error(r.data?.message || 'Failed to load substitution report');
        } catch {
            toast.error('Failed to load substitution report');
        } finally {
            setIsSubLoading(false);
        }
    }, []);

    useEffect(() => {
        loadAttendanceReport(startDate, endDate);
        loadSubstitutionReport(startDate, endDate);
    }, [loadAttendanceReport, loadSubstitutionReport]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleApply = () => {
        loadAttendanceReport(startDate, endDate);
        loadSubstitutionReport(startDate, endDate);
    };

    const rows = Array.isArray(report) ? report : [];
    const [expandedInstructors, setExpandedInstructors] = useState(new Set());
    const toggleInstructor = (id) => setExpandedInstructors(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const subRows = Array.isArray(subReport) ? subReport : [];

    const handleExport = () => {
        if (reportTab === 'attendance') {
            if (!rows.length) return;
            const headers = ['Instructor', 'Sessions Taught', 'Sessions Scheduled', 'Date', 'Class'];
            const dataRows = [];
            rows.forEach(r => {
                (r.sessions || []).forEach(s => {
                    dataRows.push([
                        r.name,
                        r.totalTaught ?? r.totalSessions,
                        r.totalScheduled ?? '',
                        new Date(s.sessionDate).toLocaleDateString('en-AU'),
                        s.className || '—',
                    ]);
                });
            });
            const csv = [headers, ...dataRows].map(row =>
                row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
            ).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `instructor-report-${startDate}-to-${endDate}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        } else {
            if (!subRows.length) return;
            const headers = ['Substitute Instructor', 'Date', 'Class', 'Scheduled Instructors'];
            const dataRows = [];
            subRows.forEach(row => {
                row.sessions.forEach(s => {
                    dataRows.push([
                        row.name,
                        new Date(s.sessionDate).toLocaleDateString('en-AU'),
                        s.className,
                        (s.scheduledInstructorNames || []).join('; '),
                    ]);
                });
            });
            const csv = [headers, ...dataRows].map(row =>
                row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(',')
            ).join('\n');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `substitution-report-${startDate}-to-${endDate}.csv`;
            link.click();
            URL.revokeObjectURL(url);
        }
    };

    const exportDisabled = reportTab === 'attendance' ? !rows.length : !subRows.length;

    return (
        <div className="ci-reports-panel">
            {/* Date range controls */}
            <div className="ci-date-range-bar">
                <div className="ci-date-range-inputs">
                    <label className="ci-date-label">From</label>
                    <input
                        type="date"
                        className="ci-date-input"
                        value={startDate}
                        onChange={e => { setStartDate(e.target.value); setActivePreset(null); }}
                    />
                    <label className="ci-date-label">To</label>
                    <input
                        type="date"
                        className="ci-date-input"
                        value={endDate}
                        onChange={e => { setEndDate(e.target.value); setActivePreset(null); }}
                    />
                    <button className="class-reports-filter-btn active" onClick={handleApply}>
                        Apply
                    </button>
                    <button className="class-reports-export-btn" onClick={handleExport} disabled={exportDisabled}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            <Icon path={mdiDownload} size={0.8} />
                            Export CSV
                        </span>
                    </button>
                </div>
                <div className="ci-date-presets">
                    {PRESETS.map(p => (
                        <button
                            key={p.id}
                            className={`class-reports-filter-btn${activePreset === p.id ? ' active' : ''}`}
                            onClick={() => {
                                const from = p.from();
                                const to = p.to();
                                setActivePreset(p.id);
                                setStartDate(from);
                                setEndDate(to);
                                loadAttendanceReport(from, to);
                                loadSubstitutionReport(from, to);
                            }}
                        >
                            {p.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Sub-tab switcher */}
            <div className="ci-report-sub-tabs">
                <button
                    className={`ci-report-sub-tab${reportTab === 'attendance' ? ' active' : ''}`}
                    onClick={() => setReportTab('attendance')}
                >
                    Sessions Taught
                </button>
                <button
                    className={`ci-report-sub-tab${reportTab === 'substitutions' ? ' active' : ''}`}
                    onClick={() => setReportTab('substitutions')}
                >
                    Substitutions
                </button>
            </div>

            {/* Attendance tab */}
            {reportTab === 'attendance' && (
                <>
                    {isLoading ? (
                        <p className="class-reports-loading">Loading report...</p>
                    ) : !rows.length ? (
                        <p className="class-reports-empty">No data available for this period</p>
                    ) : (
                        <div className="class-reports-table-wrapper">
                            <table className="class-reports-table">
                                <thead>
                                    <tr>
                                        <th>Instructor</th>
                                        <th>Sessions Taught</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => {
                                        const isOpen = expandedInstructors.has(row.id);
                                        return (
                                            <React.Fragment key={row.id}>
                                                <tr
                                                    className="ci-report-accordion-row"
                                                    onClick={() => toggleInstructor(row.id)}
                                                >
                                                    <td className="ci-report-accordion-name-cell">
                                                        <span>{row.name}</span>
                                                        <Icon path={isOpen ? mdiChevronUp : mdiChevronDown} size={0.85} color="var(--dl-color-gray-600)" />
                                                    </td>
                                                    <td className="ci-report-sessions-fraction">
                                                        {row.totalTaught ?? row.totalSessions}
                                                        {row.totalScheduled > 0 && (
                                                            <span className="ci-report-sessions-denom"> of {row.totalScheduled}</span>
                                                        )}
                                                    </td>
                                                </tr>
                                                {isOpen && (row.sessions || []).map((s, j) => (
                                                    <tr key={`${row.id}-${j}`} className="ci-report-accordion-detail">
                                                        <td className="ci-report-accordion-detail-date">
                                                            {new Date(s.sessionDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                        </td>
                                                        <td>{s.className || '—'}</td>
                                                    </tr>
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}

            {/* Substitutions tab */}
            {reportTab === 'substitutions' && (
                <div className="ci-sub-report">
                    {isSubLoading ? (
                        <p className="class-reports-loading">Loading substitution report...</p>
                    ) : !subRows.length ? (
                        <p className="class-reports-empty">No substitutions in this period</p>
                    ) : (
                        <>
                            {/* Stat cards commented out — may reinstate for future use
                            <div className="class-reports-stats-row">
                                <div className="class-reports-stat-card">
                                    <span className="class-reports-stat-value">{subRows.length}</span>
                                    <span className="class-reports-stat-label">Substitutes</span>
                                </div>
                                <div className="class-reports-stat-card">
                                    <span className="class-reports-stat-value">
                                        {subRows.reduce((s, r) => s + r.totalSessions, 0)}
                                    </span>
                                    <span className="class-reports-stat-label">Substitute Sessions</span>
                                </div>
                            </div>
                            */}

                            <div className="class-reports-table-wrapper">
                                <table className="class-reports-table">
                                    <thead>
                                        <tr>
                                            <th>Substitute Instructor</th>
                                            <th>Sessions</th>
                                            <th>Session Detail</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {subRows.map((row, i) => (
                                            <tr key={i}>
                                                <td>{row.name}</td>
                                                <td>{row.totalSessions}</td>
                                                <td>
                                                    <ul className="ci-sub-session-list">
                                                        {row.sessions.map((s, j) => (
                                                            <li key={j} className="ci-sub-session-item">
                                                                <span className="ci-sub-session-date">
                                                                    {new Date(s.sessionDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' })}
                                                                </span>
                                                                <span className="ci-sub-session-class">{s.className}</span>
                                                                {s.scheduledInstructorNames.length > 0 && (
                                                                    <span className="ci-sub-session-scheduled">
                                                                        Scheduled: {s.scheduledInstructorNames.join(', ')}
                                                                    </span>
                                                                )}
                                                            </li>
                                                        ))}
                                                    </ul>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Page ────────────────────────────────────────────────────────────────────

const ClassInstructorsPage = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    const tabs = [
        {
            label: 'Assignment',
            content: <AssignmentTab user={user} />,
        },
        {
            label: 'Reports',
            content: <ReportsTab />,
        },
    ];

    return (
        <div className="class-instructors-page dashboard-container org-console">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    <div className="sub-portal-header-card">
                        <div className="sub-portal-header-title-row">
                            <button onClick={() => navigate('/classes/manage')} className="back-nav-btn">
                                ← Manage Classes
                            </button>
                            <h1 className="sub-portal-header-title">
                                <Icon path={mdiAccountTie} size={1} color="#451f78" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                                Instructors
                            </h1>
                        </div>
                        <p className="sub-portal-header-subtitle">Assign primary instructors to classes, track their sessions, and review substitutions by temporary instructors.</p>
                    </div>

                    <FolderTabs tabs={tabs} defaultTab={0} />
                </div>
            </div>
        </div>
    );
};

export default ClassInstructorsPage;
